/**
 * Created by neo on 10.01.2026
 * Challenge Ranking Screen - Full leaderboard with teams/participants
 * Modernized: Uses react-query hooks instead of manual loading states and static API calls
 */
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import {
  useRouteChallenge,
  useIndividualChallenge,
  useIndividualChallengeParticipation,
  useChallengeRanking,
  useMyRouteChallengeTeam,
  useTeamRanking,
} from '../../Hooks/useChallengeQueries';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { Theme } from '../../Theme/Theme';
import { getMediaSmallest } from '../../Model/Media/Media';
import { ChallengeExtraMile } from './Components/ChallengeExtraMile';
import { ChallengeTeamMembers } from './Components/ChallengeTeamMembers';

const PageContainer = styled(Container)`
  padding-top: 24px;
  padding-bottom: 48px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 16px;

  &:hover {
    text-decoration: underline;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const RankingContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const RankingGrid = styled.div`
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr) 120px;
  padding: 16px 20px;

  @media (max-width: 576px) {
    grid-template-columns: 36px minmax(0, 1fr) 86px;
    column-gap: 8px;
    padding: 12px;
  }
`;

const RankingHeader = styled(RankingGrid)`
  background: ${Theme.Colors.lightestGrey};
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  font-size: 12px;
  font-weight: 600;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
`;

const RankingList = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const RankingItem = styled(RankingGrid)<{ isCurrentUser: boolean; rank: number }>`
  align-items: center;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};
  background: ${(props) => {
    if (props.isCurrentUser) return Theme.Colors.lightestPrimary;
    if (props.rank <= 3) return 'rgba(255, 215, 0, 0.05)';
    return 'white';
  }};
  transition: background 0.2s;

  &:hover {
    background: ${(props) => (props.isCurrentUser ? Theme.Colors.lightestPrimary : Theme.Colors.lightestGrey)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: ${(props) => (props.rank <= 3 ? '18px' : '14px')};
  background: ${(props) => {
    if (props.rank === 1) return 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)';
    if (props.rank === 2) return 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)';
    if (props.rank === 3) return 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)';
    return Theme.Colors.lightestGrey;
  }};
  color: ${(props) => (props.rank <= 3 ? 'white' : Theme.Colors.charcoal)};
  box-shadow: ${(props) => (props.rank <= 3 ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none')};

  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
  }
`;

const ParticipantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 576px) {
    gap: 8px;
  }
`;

const Avatar = styled.div<{ imageUrl?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(props) =>
    props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.primary};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;

  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
  }
`;

const ParticipantDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ParticipantName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ParticipantSubtext = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
`;

const PointsValue = styled.div`
  text-align: right;
  min-width: 0;
`;

const PointsNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
`;

const PointsLabel = styled.div`
  font-size: 11px;
  color: ${Theme.Colors.warmGrey};
`;

const MyRankCard = styled.div`
  background: linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MyRankInfo = styled.div``;

const MyRankLabel = styled.div`
  font-size: 13px;
  opacity: 0.9;
  margin-bottom: 4px;
`;

const MyRankValue = styled.div`
  font-size: 32px;
  font-weight: 700;
`;

const MyPointsInfo = styled.div`
  text-align: right;
`;

const MyPointsValue = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const MyPointsLabel = styled.div`
  font-size: 13px;
  opacity: 0.9;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 24px;
`;

const ErrorEmoji = styled.span`
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ErrorDescription = styled.p`
  color: ${Theme.Colors.charcoal};
`;

type ChallengeType = 'route' | 'individual';

export const ChallengeRankingScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { challengeId } = useParams<{ challengeId: string }>();

  const { data: routeChallenge, isLoading: isLoadingRoute } = useRouteChallenge(challengeId);
  const { data: individualChallenge, isLoading: isLoadingIndividual } = useIndividualChallenge(challengeId);

  const challengeType = useMemo<ChallengeType | null>(() => {
    if (individualChallenge) return 'individual';
    if (routeChallenge) return 'route';
    return null;
  }, [individualChallenge, routeChallenge]);

  const challenge = individualChallenge || routeChallenge;

  // Hook MUST be called before any early returns (Rules of Hooks)
  const challengeName = useLocalized(challenge?.name);

  const { data: myParticipation } = useIndividualChallengeParticipation(
    challengeType === 'individual' ? challengeId : undefined,
  );
  const { data: participants = [] } = useChallengeRanking(
    challengeType === 'individual' ? challengeId : undefined,
    { page: 0, size: 100 },
  );

  const { data: myTeam } = useMyRouteChallengeTeam(
    challengeType === 'route' ? challengeId : undefined,
  );
  const { data: teams = [] } = useTeamRanking(
    challengeType === 'route' ? challengeId : undefined,
    { page: 0, size: 100 },
  );

  const handleBack = useCallback(() => {
    navigate(`/motivation/challenge/${challengeId}`);
  }, [navigate, challengeId]);

  const myRank = useMemo(() => {
    if (challengeType === 'individual' && myParticipation) {
      return myParticipation.rank || participants.findIndex((p) => p.id === myParticipation.id) + 1;
    }
    if (challengeType === 'route' && myTeam) {
      return myTeam.globalRank || teams.findIndex((t) => t.id === myTeam.id) + 1;
    }
    return null;
  }, [challengeType, myParticipation, myTeam, participants, teams]);

  const myPoints = useMemo(() => {
    if (challengeType === 'individual' && myParticipation) {
      return myParticipation.totalCollectedPoints;
    }
    if (challengeType === 'route' && myTeam) {
      return myTeam.totalCollectedPoints;
    }
    return 0;
  }, [challengeType, myParticipation, myTeam]);

  if (isLoadingRoute || isLoadingIndividual) {
    return <LoadingContainer text={t('ranking.loading', { defaultValue: 'Loading leaderboard...' })} />;
  }

  if (!challenge) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorEmoji>🔍</ErrorEmoji>
          <ErrorTitle>{t('ranking.notFound', { defaultValue: 'Ranking Not Found' })}</ErrorTitle>
          <ErrorDescription>
            {t('ranking.notFoundDescription', { defaultValue: 'Could not load the ranking for this challenge.' })}
          </ErrorDescription>
        </ErrorContainer>
      </PageContainer>
    );
  }

  const isIndividual = challengeType === 'individual';

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>← {t('ranking.backToChallenge', { defaultValue: 'Back to Challenge' })}</BackButton>

      <PageHeader>
        <PageTitle>{t('ranking.title', { defaultValue: 'Leaderboard' })}</PageTitle>
        <PageSubtitle>{challengeName}</PageSubtitle>
      </PageHeader>

      {myRank && (
        <MyRankCard>
          <MyRankInfo>
            <MyRankLabel>{t('ranking.yourRank', { defaultValue: 'Your Rank' })}</MyRankLabel>
            <MyRankValue>#{myRank}</MyRankValue>
          </MyRankInfo>
          <MyPointsInfo>
            <MyPointsValue>{myPoints.toLocaleString()}</MyPointsValue>
            <MyPointsLabel>{t('ranking.points', { defaultValue: 'points' })}</MyPointsLabel>
            <ChallengeExtraMile challenge={challenge} totalPoints={myPoints} extraPoints={isIndividual ? myParticipation?.extraCollectedPoints : myTeam?.extraCollectedPoints} goalPoints={challenge.totalPossiblePoints} variant="ranking" />
          </MyPointsInfo>
        </MyRankCard>
      )}

      <RankingContainer>
        <RankingHeader>
          <span>{t('ranking.rank', { defaultValue: 'Rank' })}</span>
          <span>{isIndividual ? t('ranking.participant', { defaultValue: 'Participant' }) : t('ranking.team', { defaultValue: 'Team' })}</span>
          <span style={{ textAlign: 'right' }}>{t('ranking.points', { defaultValue: 'Points' })}</span>
        </RankingHeader>

        <RankingList>
          {isIndividual
            ? participants.map((participant, index) => {
                const rank = participant.rank || index + 1;
                const isCurrentUser = myParticipation?.id === participant.id;
                const displayName = participant.nickname || t('ranking.anonymous', { defaultValue: 'Participant' });

                return (
                  <RankingItem key={participant.id} isCurrentUser={isCurrentUser} rank={rank}>
                    <RankBadge rank={rank}>{rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}</RankBadge>
                    <ParticipantInfo>
                      <Avatar imageUrl={participant.profilePicture ? getMediaSmallest(participant.profilePicture) : undefined}>
                        {!participant.profilePicture && displayName.charAt(0).toUpperCase()}
                      </Avatar>
                      <ParticipantDetails>
                        <ParticipantName>{displayName}</ParticipantName>
                        <ParticipantSubtext>
                          {participant.reachedLocations.length}{' '}
                          {t('ranking.locationsReached', { defaultValue: 'locations reached' })}
                        </ParticipantSubtext>
                      </ParticipantDetails>
                    </ParticipantInfo>
                    <PointsValue>
                      <PointsNumber>{participant.totalCollectedPoints.toLocaleString()}</PointsNumber>
                      <PointsLabel>{t('ranking.pts', { defaultValue: 'pts' })}</PointsLabel>
                      <ChallengeExtraMile challenge={challenge} totalPoints={participant.totalCollectedPoints} extraPoints={participant.extraCollectedPoints} goalPoints={challenge.totalPossiblePoints} variant="ranking" />
                    </PointsValue>
                  </RankingItem>
                );
              })
            : teams.map((team, index) => {
                const rank = team.globalRank || index + 1;
                const isCurrentUser = myTeam?.id === team.id;

                return (
                  <RankingItem key={team.id} isCurrentUser={isCurrentUser} rank={rank}>
                    <RankBadge rank={rank}>{rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}</RankBadge>
                    <ParticipantInfo>
                      <Avatar imageUrl={team.image ? getMediaSmallest(team.image) : undefined}>
                        {!team.image && team.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <ParticipantDetails>
                        <ParticipantName><ChallengeTeamMembers team={team} challenge={challenge} /></ParticipantName>
                        <ParticipantSubtext>
                          {team.memberCount} {t('ranking.members', { defaultValue: 'members' })}
                        </ParticipantSubtext>
                      </ParticipantDetails>
                    </ParticipantInfo>
                    <PointsValue>
                      <PointsNumber>{team.totalCollectedPoints.toLocaleString()}</PointsNumber>
                      <PointsLabel>{t('ranking.pts', { defaultValue: 'pts' })}</PointsLabel>
                      <ChallengeExtraMile challenge={challenge} totalPoints={team.totalCollectedPoints} extraPoints={team.extraCollectedPoints} goalPoints={challenge.totalPossiblePoints} variant="ranking" />
                    </PointsValue>
                  </RankingItem>
                );
              })}
        </RankingList>
      </RankingContainer>
    </PageContainer>
  );
});
